import React, { useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Alert, Box, Collapse, Paper, Stack, Typography } from '@mui/material';

import ScheduleDoctorIssueSection from './ScheduleDoctorIssueSection';
import { handleExpandRowKeyDown } from './campaignScheduleTabLayout';

interface Props {
  // Schedules blocked by a market/brand eligibility rule (e.g. LuxPlus+ ineligible region) —
  // expected noise. Collapsed by default.
  rules: Array<string>;
  // Real problems with the offer's current schedules (e.g. missing a purchase schedule) that a CM
  // should act on. Expanded by default.
  genuineIssues: Array<string>;
}

// The headline badge reflects genuineIssues, not the total — rules alone are expected noise, so an
// offer with only rules and zero genuine issues shouldn't read as a yellow warning.
function headline(
  rules: Array<string>,
  genuineIssues: Array<string>,
): { severity: 'success' | 'warning' | 'info'; text: string } {
  if (genuineIssues.length > 0) {
    return {
      severity: 'warning',
      text: `${genuineIssues.length} issue${genuineIssues.length === 1 ? '' : 's'} detected`,
    };
  }
  if (rules.length > 0) {
    return { severity: 'info', text: `${rules.length} rule${rules.length === 1 ? '' : 's'}, no issues` };
  }
  return { severity: 'success', text: 'No issues detected' };
}

export default function ScheduleDoctor({ rules, genuineIssues }: Props) {
  const [expanded, setExpanded] = useState(false);
  const hasIssues = rules.length + genuineIssues.length > 0;
  const { severity: headlineSeverity, text: headlineText } = headline(rules, genuineIssues);
  const toggle = () => setExpanded((current) => !current);

  return (
    <Paper sx={{ bgcolor: 'background.paper', borderRadius: 3 }} variant="outlined">
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        role={hasIssues ? 'button' : undefined}
        spacing={2}
        sx={{ cursor: hasIssues ? 'pointer' : 'default', px: 3, py: 2 }}
        tabIndex={hasIssues ? 0 : undefined}
        onClick={() => hasIssues && toggle()}
        onKeyDown={hasIssues ? (event) => handleExpandRowKeyDown(event, toggle) : undefined}
      >
        <Stack alignItems="center" direction="row" spacing={1}>
          {expanded ? (
            <KeyboardArrowDownIcon color={hasIssues ? undefined : 'disabled'} fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon color={hasIssues ? undefined : 'disabled'} fontSize="small" />
          )}
          <Typography fontWeight={700} variant="h3">
            Schedule Doctor
          </Typography>
        </Stack>
        <Alert severity={headlineSeverity} sx={{ py: 0 }}>
          {headlineText}
        </Alert>
      </Stack>

      {hasIssues && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 3, pb: 2 }}>
            <ScheduleDoctorIssueSection
              defaultExpanded
              issues={genuineIssues}
              itemLabel="issue"
              severity="warning"
              title="Issues"
            />
            <ScheduleDoctorIssueSection
              defaultExpanded={false}
              issues={rules}
              itemLabel="rule"
              severity="info"
              title="Rules"
            />
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}
